import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { cn } from '@rocco/utils/cn';
import { Separator } from '@rocco/components/separator';
import type {
    ChatRecipient,
    ContactSuggestion,
    Conversation,
} from '../../types';
import { ConversationProvider, useConversation } from '../../context';
import { MAX_PARTICIPANTS } from '../../utils';
import { useChatBoxes } from '../../hooks';
import { useContactSuggestions } from '../../hooks/use-contact-suggestions';
import { useCreateConversation } from '../../hooks/use-create-conversation';
import { ChatInput, type ChatInputHandle } from '../chat-input/ChatInput';
import { ChatBoxRecipients } from './ChatBoxRecipients';
import { ChatBoxSuggestionsList } from './ChatBoxSuggestionsList';
import { ChatBoxMessages } from './ChatBoxMessages';

/**
 * The suggestions list, messages, and input for the new message chat box.
 * In forward mode, allows selecting an existing conversation to forward to.
 * @see {@link ChatBox}
 */
export function ChatBoxNewMessage({
    className,
    chatInputRef,
    isOpen,
}: {
    className?: string;
    chatInputRef?: React.Ref<ChatInputHandle>;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
}) {
    const {
        newMessageChatBoxState: { recipients, selectedConversation },
    } = useChatBoxes();
    const { memberIDs, professionalIDs } = useMemo(() => {
        if (selectedConversation) {
            return { memberIDs: [], professionalIDs: [] };
        }
        return {
            memberIDs: recipients
                .filter((r) => r.__typename === 'Member')
                .map((r) => r.ID),
            professionalIDs: recipients
                .filter((r) => r.__typename === 'Professional')
                .map((r) => r.ID),
        };
    }, [selectedConversation, recipients]);

    return (
        <ConversationProvider
            conversationID={
                selectedConversation ? Number(selectedConversation.ID) : 0
            }
            professionalID={null}
            participantMemberIDs={memberIDs}
            participantProfessionalIDs={professionalIDs}
            conversation={selectedConversation ?? undefined}
            isPollingDisabled
        >
            <ChatBoxNewMessageContent
                className={className}
                chatInputRef={chatInputRef}
                isOpen={isOpen}
            />
        </ConversationProvider>
    );
}

function ChatBoxNewMessageContent({
    className,
    chatInputRef,
    isOpen,
}: {
    className?: string;
    chatInputRef?: React.Ref<ChatInputHandle>;
    isOpen: boolean;
}) {
    const [search, setSearch] = useState('');
    const [scrollDownCounter, setScrollDownCounter] = useState(0);
    const searchInputRef = useRef<HTMLInputElement>();
    const {
        newMessageChatBoxState: {
            recipients,
            selectedConversation,
            pendingRecipientIDs,
        },
        forwardedMessage,
        setNewMessageRecipients,
        setNewMessageConversation,
        setPendingMessage,
        setForwardedMessage,
        convertNewMessageToConversation,
    } = useChatBoxes();
    const isForwardMode = !!forwardedMessage;
    const { memberIDs, professionalIDs } = useMemo(() => {
        if (selectedConversation) {
            return { memberIDs: [], professionalIDs: [] };
        }
        return {
            memberIDs: recipients
                .filter((r) => r.__typename === 'Member')
                .map((r) => r.ID),
            professionalIDs: recipients
                .filter((r) => r.__typename === 'Professional')
                .map((r) => r.ID),
        };
    }, [selectedConversation, recipients]);
    const {
        suggestions,
        conversations,
        hasMore: hasMoreSuggestions,
        isLoading: isLoadingSuggestions,
        setSearch: setSearchSuggestions,
        loadMore,
    } = useContactSuggestions({ limit: 50, includeChats: isForwardMode });
    const {
        conversation: conversationPreview,
        messages: messagesPreview,
        isLoading,
        isSubmitting: isSendingMessage,
    } = useConversation();
    const {
        createdConversation,
        optimisticMessage,
        isSubmitting: isCreatingConversation,
        createConversation,
    } = useCreateConversation({
        memberIDs,
        professionalIDs,
        conversation: conversationPreview,
    });
    /**
     * The active conversation matching the selected recipients or selected
     * conversation. This is `null` if there is no existing conversation
     * matching the selected recipients.
     */
    const activeConversation =
        conversationPreview ?? selectedConversation ?? null;
    const messages = useMemo(() => {
        const serverMessages =
            messagesPreview.length > 0
                ? messagesPreview
                : (selectedConversation?.Messages.nodes ?? []);
        if (optimisticMessage) {
            return [optimisticMessage, ...serverMessages];
        }
        return serverMessages;
    }, [messagesPreview, selectedConversation, optimisticMessage]);

    const handleInputRef = useCallback((element?: HTMLInputElement) => {
        searchInputRef.current = element;
    }, []);

    const handleSearchChange = useCallback(
        (search: string, excludeMemberIDs?: number[]) => {
            setSearch(search);
            setSearchSuggestions(search, excludeMemberIDs);
        },
        [setSearchSuggestions],
    );

    function handleRecipientsChange(nextRecipients: ChatRecipient[]) {
        setNewMessageRecipients(nextRecipients);
        handleSearchChange(
            search,
            nextRecipients
                .filter((r) => r.__typename === 'Member')
                .map((r) => r.ID),
        );
    }

    function addRecipient(suggestion: ContactSuggestion) {
        if (
            recipients.some(
                (r) =>
                    r.__typename === suggestion.__typename &&
                    r.ID === suggestion.ID,
            ) ||
            recipients.length >= MAX_PARTICIPANTS
        ) {
            return;
        }
        const newRecipients = [...recipients, suggestion];
        setNewMessageRecipients(newRecipients);
        handleSearchChange(
            '',
            newRecipients
                .filter((r) => r.__typename === 'Member')
                .map((r) => r.ID),
        );
        searchInputRef.current?.focus();
    }

    function handleConversationSelect(conversation: Conversation) {
        setNewMessageConversation(conversation);
        handleSearchChange('');
    }

    // Resolve pending recipient IDs once contact suggestions are available.
    useEffect(() => {
        if (
            isOpen &&
            pendingRecipientIDs.length > 0 &&
            suggestions.length > 0
        ) {
            const preselected = suggestions.filter((suggestion) =>
                pendingRecipientIDs.includes(suggestion.ID),
            );

            if (preselected.length > 0) {
                setNewMessageRecipients(preselected);
                handleSearchChange(
                    '',
                    preselected.map((r) => r.ID),
                );
            }
        }
    }, [
        isOpen,
        pendingRecipientIDs,
        suggestions,
        setNewMessageRecipients,
        handleSearchChange,
    ]);

    // Convert this new message chat box to a normal conversation chat box when
    // a new conversation is created.
    useEffect(() => {
        if (createdConversation) {
            convertNewMessageToConversation(
                Number(createdConversation.ID),
                null,
            );
        }
    }, [createdConversation, convertNewMessageToConversation]);

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <ChatBoxRecipients
                search={search}
                recipients={recipients}
                conversation={selectedConversation ?? undefined}
                maxRecipients={MAX_PARTICIPANTS}
                isOpen={isOpen}
                isDisabled={isCreatingConversation}
                onSearchChange={handleSearchChange}
                onRecipientsChange={handleRecipientsChange}
                onConversationRemove={() => setNewMessageConversation(null)}
                onInputRef={handleInputRef}
            />
            <Separator className="bg-black/20" />
            {!selectedConversation &&
            (recipients.length === 0 ||
                (search && recipients.length < MAX_PARTICIPANTS)) ? (
                <ChatBoxSuggestionsList
                    className="mt-[8px] min-h-0 flex-1"
                    isLoading={isLoadingSuggestions}
                    suggestions={suggestions}
                    conversations={isForwardMode ? conversations : undefined}
                    hasMore={hasMoreSuggestions}
                    onSuggestionClick={addRecipient}
                    onConversationClick={handleConversationSelect}
                    onLoadMore={loadMore}
                />
            ) : (
                <>
                    <ChatBoxMessages
                        className="min-h-0 flex-1"
                        messages={messages}
                        isOpen={isOpen}
                        scrollDownCounter={scrollDownCounter}
                        hideActions
                    />
                    <ChatInput
                        ref={chatInputRef}
                        isOpen={isOpen}
                        isSubmitting={
                            selectedConversation
                                ? isSendingMessage
                                : isCreatingConversation || isLoading
                        }
                        autoFocus={!!selectedConversation}
                        replyingTo={forwardedMessage}
                        replyMode="forward"
                        onCancelReply={() => {
                            setForwardedMessage(null);
                            if (selectedConversation) {
                                setNewMessageConversation(null);
                            }
                        }}
                        onSend={({ message, attachments, replyTo }) => {
                            if (activeConversation) {
                                const conversationID = Number(
                                    activeConversation.ID,
                                );
                                setPendingMessage({
                                    conversationID,
                                    professionalID: null,
                                    message,
                                    attachments,
                                    replyTo,
                                });
                                convertNewMessageToConversation(
                                    conversationID,
                                    null,
                                );
                            } else {
                                // This new message chat box will be converted to
                                // a normal conversation chat box when this action
                                // is completed (see useEffect above).
                                createConversation(
                                    message,
                                    attachments,
                                    replyTo,
                                );
                            }
                            // Clear forwarding state after sending.
                            setForwardedMessage(null);
                        }}
                        onAttachmentsChanged={() => {
                            setScrollDownCounter((state) => state + 1);
                        }}
                    />
                </>
            )}
        </div>
    );
}
